import type { SpeciesId } from "./species.ts";

/**
 * Level-up offers after the starting kit, merged into each species' learnset (species.ts).
 *
 * Every line gets something to look forward to after it evolves: a coverage attack that hits what
 * walls its own types (about a fifth of the way through its levels), a stronger same-type move when
 * its kit tops out below 90 power (half way), and a tool that fits its stats (four fifths of the
 * way): priority for fast lines, recovery for bulky ones, a condition rider or a setup move for the
 * rest. Base forms that would otherwise evolve without any coverage get one offer before evolving.
 * Legends get a second coverage type late. Wild memelings mint with the offers their level has
 * reached (kitAtLevel in game/mon.ts), so deeper grass uses them too; trainer kits stay as designed.
 */
export const LEVEL_UP: Partial<Record<SpeciesId, readonly (readonly [number, string])[]>> = {
  // Starters: Tavi, Pella and Brin lean on VIRAL, LIQUID and CHAOS, so both COPE starters get an
  // early answer: Wojakle before Tavi, Bogfiend before Pella. Shibonk already has Moon Howl.
  bogfiend: [[8, "goldRush"]],
  wojakle: [[6, "undertow"]],
  gigachomp: [[17, "sadViolin"], [30, "goldChain"], [42, "yesAnd"]],
  whalord: [[20, "goViral"], [32, "slippageSurge"], [43, "riptide"]],
  rugfox: [[14, "sadViolin"]],
  moonling: [[15, "goViral"]],
  shibonkx: [[22, "undertow"], [33, "trendingNow"], [43, "hotTake"]],
  bogbaron: [[34, "meltdown"], [44, "napTime"]],
  wojaked: [[22, "wreckingBall"], [33, "meltdown"], [43, "rumorMill"]],
  laserhound: [[34, "trendingNow"], [44, "hotTake"]],
  rugwolf: [[25, "goViral"], [35, "mempoolCrush"], [44, "frontRun"]],
  apemajor: [[35, "trendingNow"], [44, "hypeTrain"]],
  moonlord: [[27, "emberRain"], [36, "oracleBeam"], [44, "pingSpike"]],
  chopperalpha: [[27, "frostbite"], [36, "slippageSurge"], [44, "hypeTrain"]],
  cloutking: [[33, "airdropStrike"], [43, "hotTake"]],
  copiumax: [[24, "frostbite"], [34, "meltdown"], [44, "rumorMill"]],
  candlewyrm: [[25, "goViral"], [35, "mempoolCrush"], [44, "rumorMill"]],
  diamondshell: [[23, "emberRain"], [34, "slippageSurge"], [43, "warmReserve"]],
  fomobeast: [[35, "trendingNow"], [44, "hotTake"]],
  gasgob: [[18, "sadViolin"], [31, "mempoolCrush"], [42, "rumorMill"]],
  slippeel: [[18, "emberRain"], [31, "slippageSurge"], [42, "flashLoan"]],
  airdropqueen: [[24, "undertow"], [34, "goldChain"], [44, "quickFlip"]],
  yieldyeti: [[21, "goViral"], [33, "slippageSurge"], [43, "riptide"]],
  yapstorm: [[22, "frostbite"], [33, "trendingNow"], [43, "flashCrash"]],
  lurkit: [[13, "rootQuake"]],
  threadmaw: [[24, "chaosTheory"], [34, "meltdown"], [44, "pingSpike"]],
  ratiofin: [[10, "sadViolin"]],
  ratioking: [[23, "goViral"], [34, "oracleBeam"], [43, "pingSpike"]],
  scrollmite: [[15, "frostbite"]],
  doomwyrm: [[25, "rootQuake"], [35, "meltdown"], [44, "napTime"]],
  finekit: [[14, "emberRain"]],
  kilnmutt: [[23, "chaosTheory"], [34, "meltdown"], [43, "touchGrass"]],
  lorelord: [[25, "meteorShower"], [35, "airdropStrike"], [44, "pingSpike"]],
  gatekit: [[11, "sadViolin"]],
  gatewyrm: [[22, "overfit"], [33, "oracleBeam"], [43, "pingSpike"]],
  glazeling: [[15, "vanishingAct"]],
  glazewyrm: [[24, "undertow"], [34, "trendingNow"], [44, "toxicThread"]],
  capling: [[9, "broadcast"]],
  nocapra: [[22, "undertow"], [33, "airdropStrike"], [43, "hypeTrain"]],
  grassnudge: [[12, "meteorShower"]],
  lawnlord: [[23, "undertow"], [34, "goldChain"], [43, "takeFive"]],
  seethtoad: [[13, "frostbite"]],
  maldmaw: [[24, "wreckingBall"], [34, "meltdown"], [44, "napTime"]],
  deluling: [[13, "goViral"]],
  delulord: [[35, "meltdown"], [44, "touchGrass"]],
  vibeling: [[10, "meteorShower"]],
  vibewyrm: [[22, "undertow"], [33, "trendingNow"], [43, "hotTake"]],
  draftkit: [[12, "rootQuake"]],
  postwyrm: [[23, "emberRain"], [34, "meltdown"], [43, "pingSpike"]],
  spoilpup: [[12, "sadViolin"]],
  leakmaw: [[24, "overfit"], [34, "oracleBeam"], [44, "rumorMill"]],
  queueling: [[9, "chaosTheory"]],
  linebaron: [[22, "emberRain"], [33, "slippageSurge"], [43, "marginBite"]],
  receiptling: [[15, "goViral"]],
  auditlord: [[24, "meteorShower"], [34, "airdropStrike"], [44, "patchNotes"]],
  clipfinch: [[13, "broadcast"]],
  cliplord: [[25, "frostbite"], [35, "mempoolCrush"], [44, "hotTake"]],
  toquelin: [[9, "chaosTheory"]],
  tuquelord: [[22, "emberRain"], [33, "goldChain"], [43, "napTime"]],
  poutkit: [[12, "vanishingAct"]],
  gravylord: [[23, "overfit"], [34, "slippageSurge"], [43, "deepBuy"]],
  ghostkit: [[13, "rootQuake"]],
  ghostwyrm: [[24, "goldRush"], [34, "meltdown"], [44, "copiumJab"]],
  crashpup: [[13, "broadcast"]],
  crashmaw: [[24, "frostbite"], [34, "mempoolCrush"], [44, "toxicThread"]],
  sauceling: [[9, "emberRain"]],
  saucelord: [[22, "goViral"], [33, "goldChain"], [43, "rumorMill"]],
  pingling: [[15, "sadViolin"]],
  pinglord: [[25, "rootQuake"], [35, "oracleBeam"], [44, "frontRun"]],
  slayling: [[9, "undertow"]],
  slaylord: [[22, "meteorShower"], [33, "airdropStrike"], [43, "toxicThread"]],
  cookling: [[11, "goViral"]],
  cookwyrm: [[22, "sadViolin"], [33, "airdropStrike"], [43, "rumorMill"]],
  midling: [[12, "goldRush"]],
  midbaron: [[23, "rootQuake"], [34, "meltdown"], [43, "patchNotes"]],
  lockkit: [[15, "goViral"]],
  locklord: [[24, "meteorShower"], [34, "goldChain"], [44, "pingSpike"]],
  depkit: [[12, "overfit"]],
  depbaron: [[23, "vanishingAct"], [34, "slippageSurge"], [43, "rumorMill"]],
  ehcorn: [[9, "undertow"]],
  ehvergreen: [[22, "broadcast"], [33, "trendingNow"], [43, "rareTreat"]],
  snoozlet: [[12, "frostbite"]],
  napocalypse: [[23, "wreckingBall"], [34, "meltdown"], [43, "napTime"]],
  coneling: [[13, "emberRain"]],
  detourant: [[24, "sadViolin"], [34, "mempoolCrush"], [44, "lidOn"]],
  syrupup: [[12, "frostbite"]],
  sapreme: [[23, "chaosTheory"], [34, "trendingNow"], [43, "hypeTrain"]],
  bufferret: [[15, "goldRush"]],
  lagomorph: [[25, "rootQuake"], [35, "oracleBeam"], [44, "copiumJab"]],
  honklet: [[9, "overfit"]],
  honkference: [[22, "vanishingAct"], [33, "slippageSurge"], [43, "patchNotes"]],
  chillip: [[12, "chaosTheory"]],
  chillossus: [[23, "overfit"], [34, "slippageSurge"], [43, "napTime"]],
  crumbit: [[12, "undertow"]],
  breadwinner: [[23, "meteorShower"], [34, "trendingNow"], [43, "hypeTrain"]],
  puckup: [[13, "goViral"]],
  clapback: [[24, "sadViolin"], [34, "airdropStrike"], [44, "rumorMill"]],
  tabbit: [[15, "wreckingBall"]],
  tabalanche: [[25, "sadViolin"], [35, "oracleBeam"], [44, "frontRun"]],
  frostbag: [[25, "overfit"]],
  glacierbag: [[34, "meteorShower"], [40, "blizzardBag"], [46, "longWinter"]],
  pengwallet: [[26, "goViral"]],
  coldvault: [[35, "overfit"], [41, "blizzardBag"], [46, "longWinter"]],
  diamondpaws: [[34, "meteorShower"], [41, "neverSell"], [46, "iceAge"]],
  candlecub: [[24, "broadcast"]],
  wickwolf: [[34, "chaosTheory"], [40, "scorchedEarth"], [46, "bullRun"]],
  shillpion: [[24, "frostbite"]],
  shillscorp: [[34, "vanishingAct"], [41, "scorchedEarth"], [46, "meterUp"]],
  sprikle: [[24, "wreckingBall"]],
  saguarocket: [[34, "frostbite"], [40, "greenCandle"], [46, "burnAddress"]],
  humpfund: [[27, "meteorShower"], [36, "blizzardBag"], [44, "yesAnd"]],
  blazebit: [[30, "broadcast"]],
  infernomint: [[39, "wreckingBall"], [43, "scorchedEarth"], [47, "meterUp"]],
  gasbull: [[25, "vanishingAct"]],
  magmabull: [[35, "chaosTheory"], [41, "scorchedEarth"], [46, "rekindle"]],
  moltwisp: [[32, "chaosTheory"], [39, "greenCandle"], [46, "priorityFee"]],
  ruglet: [[27, "goViral"]],
  rugwraith: [[34, "sadViolin"], [46, "rumorMill"]],
  mirebot: [[26, "emberRain"]],
  mireshill: [[34, "chaosTheory"], [41, "phantomMint"], [46, "riptide"]],
  botfly: [[26, "undertow"]],
  swarmlord: [[33, "frostbite"], [40, "delisting"], [46, "toxicThread"]],
  honeytoad: [[27, "emberRain"]],
  honeybaron: [[35, "goldRush"], [46, "napTime"]],
  shardling: [[28, "overfit"]],
  shardking: [[36, "rootQuake"], [42, "glacierBeam"], [47, "patchNotes"]],
  prismpup: [[27, "goViral"]],
  prismhound: [[34, "goldRush"], [40, "blizzardBag"], [46, "longWinter"]],
  nodelet: [[29, "emberRain"]],
  validrake: [[35, "goViral"], [41, "delisting"], [46, "pingSpike"]],
  cachemole: [[26, "broadcast"]],
  vaultmole: [[34, "rootQuake"], [41, "glacierBeam"], [46, "daydream"]],
  genesiwyrm: [[41, "overfit"], [44, "marginCall"], [48, "priorityFee"], [49, "rootQuake"]],
  ursarug: [[41, "undertow"], [48, "shadowFork"], [49, "wreckingBall"]],
  chatlord: [[37, "broadcast"], [42, "neuralBeam"], [47, "zeroShot"]],
  glitchhound: [[38, "wreckingBall"], [43, "neuralBeam"], [47, "bottleCap"]],
  fanstorm: [[38, "goldRush"], [43, "greenCandle"], [47, "fineTune"]],
  rackmoth: [[37, "goldRush"], [42, "neuralBeam"], [47, "zeroShot"]],
  neuralith: [[42, "goldRush"], [48, "rocketFuel"], [49, "vanishingAct"]],
  stuckwing: [[36, "goViral"], [42, "phantomMint"], [47, "insiderTip"]],
  shroomancer: [[37, "frostbite"], [42, "meltdown"], [47, "loopClip"]],
  asicarab: [[36, "undertow"], [42, "neverSell"], [47, "reboot"]],
  geodrake: [[38, "meteorShower"], [43, "neverSell"], [47, "lidOn"]],
  krillionaire: [[36, "vanishingAct"], [42, "airdropStrike"], [47, "grindset"]],
  crabstack: [[37, "emberRain"], [42, "slippageSurge"], [47, "deepBuy"]],
  medusell: [[37, "chaosTheory"], [42, "delisting"], [47, "flashLoan"]],
  pearlclam: [[35, "overfit"], [41, "wenMoon"], [46, "harvestMoon"]],
  lunawolf: [[37, "chaosTheory"], [42, "wenMoon"]],
  rocketitan: [[39, "vanishingAct"], [47, "bullRun"]],
  nimbram: [[37, "broadcast"], [42, "wenMoon"], [47, "longWinter"]],
  craterling: [[39, "overfit"], [43, "wenMoon"], [47, "nightSiphon"]],
  selenarch: [[42, "sadViolin"], [45, "monolithSlam"], [48, "longWinter"], [49, "rootQuake"]],
  gourdian: [[47, "blackCandle"], [50, "compoundGrowth"]],
  crowllateral: [[45, "emberRain"], [47, "capitulation"], [50, "quickFlip"]],
  nutvault: [[44, "goldRush"], [46, "oldGrowth"], [49, "longWinter"]],
  rektoplasm: [[46, "broadcast"], [50, "hotTake"]],
  cryptkeep: [[47, "undertow"], [49, "capitulation"], [50, "hauntedChart"]],
  arachnft: [[46, "emberRain"], [48, "blackCandle"], [50, "grindset"]],
  catacomb: [[47, "emberRain"], [50, "deathSpiral"]],
  beakfolio: [[48, "undertow"], [49, "bullRun"], [50, "oldGrowth"]],
  vineconda: [[48, "undertow"], [49, "insiderTip"], [50, "phantomMint"]],
  slothstake: [[49, "broadcast"]],
  ryukoin: [[50, "riptide"]],
  craneprint: [[48, "orbitKick"], [49, "vanishingAct"], [50, "wenMoon"]],
  bonsaitoise: [[49, "goldRush"], [50, "iceAge"]],
  colosstake: [[49, "yieldFarm"], [50, "meteorShower"]],
  sphynxfi: [[48, "flashCrash"], [49, "capitulation"], [50, "goViral"]],
  wrappharaoh: [[49, "insiderTip"], [50, "meteorShower"]],
  arborealm: [[48, "sadViolin"], [49, "rocketFuel"], [50, "goldRush"]],
  rektquiem: [[48, "sadViolin"], [49, "bottleCap"], [50, "goViral"]],
};
